import { AlertTriangle } from "lucide-react";
import { fmtDateTime } from "../lib/format";

const TYPES = [
  ["sms_ready", "Klar til afhentning", "Når afhentningen åbner."],
  ["sms_reminder", '"Sidste chance"-påmindelse', "Kort før afhentningen lukker."],
];

const on = (v) => v !== "0";
export const kr = (ore) => `${(ore / 100).toFixed(2).replace(".", ",")} kr.`;

export function smsStatus(form) {
  if (form.sms_enabled !== "1") return "slået fra";
  const n = TYPES.filter(([k]) => on(form[k])).length;
  return n === TYPES.length ? "aktive" : `aktive (${n} af ${TYPES.length} typer)`;
}

export function SmsAdminNote({ info }) {
  if (!info) return null;
  return (
    <>
      {!info.configured && (
        <p className="notice warn" style={{ fontSize: 13 }} data-testid="sms-admin-not-configured">
          Kun admin: Twilio er ikke sat op på serveren – SMS sendes ikke rigtigt, kun i testtilstand.
        </p>
      )}
      {info.failed_7d > 0 && (
        <p className="notice error" style={{ fontSize: 13, display: "flex", gap: 6 }} data-testid="sms-admin-failures">
          <AlertTriangle size={16} style={{ flex: "none" }} />
          <span>
            Kun admin: {info.failed_7d} SMS fejlede de sidste 7 dage ({info.failed_store_7d} i denne butik).
            {info.last_error && <> Seneste fejl {fmtDateTime(info.last_error_at)}: «{info.last_error.slice(0, 160)}»</>}
          </span>
        </p>
      )}
    </>
  );
}

function SmsUsage({ q }) {
  const pct = Math.min(100, Math.round((q.used / Math.max(1, q.limit)) * 100));
  return (
    <div className="sms-usage" data-testid="sms-usage">
      <div className="su-head">
        <b data-testid="sms-usage-label">{q.used} af {q.limit} SMS brugt i {q.month}</b>
        {q.extra_used > 0 && (
          <span className="muted" data-testid="sms-usage-extra">+ {q.extra_used} ekstra ({kr(q.extra_cost_ore)} ekskl. moms)</span>
        )}
      </div>
      <div className={"su-bar" + (pct >= 100 ? " full" : pct >= 80 ? " warn" : "")} role="progressbar" aria-valuenow={q.used} aria-valuemin={0} aria-valuemax={q.limit}>
        <span style={{ width: `${pct}%` }} />
      </div>
      <div className="hint">{q.limit} SMS pr. måned er inkluderet. Tælleren nulstilles den 1. i hver måned.</div>
    </div>
  );
}

function SmsNotice({ q }) {
  if (q.reason === "trial")
    return <p className="notice warn" data-testid="sms-trial-note">SMS er ikke med i prøveperioden – de starter, når abonnementet er aktivt.</p>;
  if (q.reason === "locked")
    return <p className="notice warn" data-testid="sms-locked-note">Der sendes ikke SMS, mens adgangen er låst.</p>;
  if (q.reason === "quota")
    return (
      <p className="notice warn" data-testid="sms-quota-note">
        {q.extra_on ? "Loftet for ekstra SMS er nået" : `Alle ${q.limit} SMS i ${q.month} er brugt`} – der sendes ikke flere SMS i denne måned. Kunderne får stadig besked på Messenger.
      </p>
    );
  return null;
}

export default function SmsSettings({ form, setForm, set, quota }) {
  const toggle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked ? "1" : "0" }));
  const master = form.sms_enabled === "1";
  const extra = form.sms_extra === "1";
  return (
    <div className="sms-box" data-testid="sms-settings">
      <h3 style={{ marginTop: 6 }}>SMS til kunder</h3>
      <label className="check" style={{ marginBottom: 6 }}>
        <input type="checkbox" checked={master} onChange={toggle("sms_enabled")} data-testid="sms-master-toggle" /> Send SMS-påmindelser til kunder
      </label>
      <div className="hint" style={{ marginBottom: 10 }}>
        Kunder, der har skrevet deres telefonnummer på ordresiden, får SMS, når varen er klar, og en påmindelse inden afhentningen lukker. Bekræftelser og besked fra ventelisten sendes kun via Messenger. SMS er slået fra, indtil du selv slår dem til her. Så længe det er slået fra, sendes ingen SMS, og kunderne bliver ikke bedt om et nummer.
      </div>
      {master && (
        <div className="sms-types">
          {quota && <SmsNotice q={quota} />}
          {TYPES.map(([k, label, hint]) => (
            <label className="check" key={k}>
              <input type="checkbox" checked={on(form[k])} onChange={toggle(k)} data-testid={`sms-toggle-${k}`} />{" "}
              <span>{label} <span className="muted" style={{ fontSize: 13 }}>– {hint}</span></span>
            </label>
          ))}
          {on(form.sms_reminder) && (
            <div className="field" style={{ marginTop: 8 }}>
              <label htmlFor="rbe">"Sidste chance"-SMS</label>
              <input id="rbe" type="number" min={0} max={24} value={form.reminder_before_end_hours || ""} onChange={set("reminder_before_end_hours")} style={{ maxWidth: 120 }} data-testid="sms-reminder-hours" />
              <div className="hint">timer før afhentningen lukker (0 = slået fra).</div>
            </div>
          )}
          {quota && !quota.billing_exempt && (
            <>
              <SmsUsage q={quota} />
              <label className="check" style={{ marginTop: 10 }}>
                <input type="checkbox" checked={extra} onChange={toggle("sms_extra")} data-testid="sms-extra-toggle" />{" "}
                <span>Tillad ekstra SMS <span className="muted" style={{ fontSize: 13 }}>({kr(quota.extra_price_ore)} ekskl. moms pr. SMS – lægges på næste faktura)</span></span>
              </label>
              <div className="hint">Slået fra: når de {quota.limit} SMS er brugt, stopper SMS'erne resten af måneden.</div>
              {extra && (
                <div className="field" style={{ marginTop: 8 }}>
                  <label htmlFor="sxc">Maks. ekstra SMS pr. måned</label>
                  <input id="sxc" type="number" min={0} inputMode="numeric" placeholder="Ingen grænse" value={form.sms_extra_cap || ""} onChange={set("sms_extra_cap")} style={{ maxWidth: 160 }} data-testid="sms-extra-cap" />
                  <div className="hint">Tomt = ingen grænse.</div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
